import type { AuthHeadersFn, EdgeCommand, EdgeCommandKind, EdgeLoraStatus, EdgeNode, EdgeNodeList, EdgePairing } from './edgeTypes';
import { API_URL } from '../../config';

/** Carries the HTTP status so the panel can tell 'no group' (403) from a failure. */
export class EdgeApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(url: string, method: string, headers: HeadersInit, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }));
    throw new EdgeApiError(err.detail || `Request failed: ${response.status}`, response.status);
  }
  return response.json();
}

const BASE = () => `${API_URL}/api/edge/nodes`;

export interface EdgeNodeCreate {
  name: string;
  latitude?: number;
  longitude?: number;
}

export function fetchEdgeNodes(h: AuthHeadersFn): Promise<EdgeNodeList> {
  return request(BASE(), 'GET', h());
}

export function createEdgeNode(h: AuthHeadersFn, body: EdgeNodeCreate): Promise<EdgePairing> {
  return request(BASE(), 'POST', h(true), body);
}

export function updateEdgeNode(
  h: AuthHeadersFn,
  id: number,
  patch: { name?: string; latitude?: number; longitude?: number; lora_gateway?: boolean },
): Promise<EdgeNode> {
  return request(`${BASE()}/${id}`, 'PATCH', h(true), patch);
}

/** Revokes the node's current token immediately — confirm before calling. */
export function issueEdgePairing(h: AuthHeadersFn, id: number): Promise<EdgePairing> {
  return request(`${BASE()}/${id}/pairing-code`, 'POST', h(true));
}

export function setEdgeQuarantine(h: AuthHeadersFn, id: number, quarantined: boolean): Promise<EdgeNode> {
  return request(`${BASE()}/${id}/quarantine`, 'POST', h(true), { quarantined });
}

export function deleteEdgeNode(h: AuthHeadersFn, id: number): Promise<{ deleted: number }> {
  return request(`${BASE()}/${id}`, 'DELETE', h());
}

export function fetchLoraStatus(h: AuthHeadersFn): Promise<EdgeLoraStatus> {
  return request(`${API_URL}/api/edge/lora/status`, 'GET', h());
}

/** Signed server-side; the node verifies, refuses replays, and acks over LoRa. */
export function issueEdgeCommand(h: AuthHeadersFn, id: number, kind: EdgeCommandKind, arg = ''): Promise<EdgeCommand> {
  return request(`${BASE()}/${id}/commands`, 'POST', h(true), { kind, arg });
}

export function fetchEdgeCommands(h: AuthHeadersFn, id: number): Promise<{ commands: EdgeCommand[] }> {
  return request(`${BASE()}/${id}/commands`, 'GET', h());
}
